import React, { useEffect, useState } from "react";
import { DataTable, DataTableCustomActions, DataTableField } from '../../../../components/data/DataTable';
import { API_ROUTES } from '../../../../core/Constants';
import { hasRole, setPage, toPrettyDate, useGlobal, useHub } from '../../../../core/Global';
import Icon from "../../../../components/base/Icon";
import Modal from "../../../../components/base/Modal";
import FlexRow from "../../../../components/base/FlexRow";
import PhoneLine from "../models/PhoneLine";
import { toastSuccess } from "../../../../components/base/Toast";

function LinesReleased() {

  const hub = useHub()
  const [selectendLine, selectendLineSet] = useState<PhoneLine>();
  const [refresh, refreshSet] = useState<boolean>();

  // Why Twilio would not give the number back, for the window that offers deleting the record.
  const [restoreError, restoreErrorSet] = useState<string>();

  const reload = () => {
    refreshSet(true);
    setTimeout(() => refreshSet(false), 500);
  }

  const LineCompany = (data: PhoneLine) => (
    <div>
      <div>
        {data.company?.name}
      </div>
      <div className="d-flex align-items-center mt-1">
        <Icon icon="user" className="me-1" type="solid" size={11} />
        <div className="fs-8">
          {data.ownerUser?.fullName}
        </div>
      </div>
    </div>
  )

  const ReleaseNumberAction = (data: any) => (
    <>
      {
        hasRole("RestoreNumber") &&
        <div className="menu-item">
          <a href="#" className="menu-link px-3"
            onClick={e => {
              e.preventDefault();
              selectendLineSet(data);
              window.openModal("modal-restore-number");
            }}>
            <Icon icon="redo" className="me-2" />
            Restore Number
          </a>
        </div>
      }
    </>
  )

  const ViewNumberLogs = (data: any) => (
    <div className="menu-item">
      <a className="menu-link px-3">
        <Icon icon="file-lines" className="me-2" />
        View Logs
      </a>
    </div>
  )

  const CreatedItem = (data: PhoneLine) => (
    <div>
      <div className='d-flex align-items-center'>
        {toPrettyDate(data.purchasedAt)}
      </div>
      {
        data.creatorUser &&
        <FlexRow className='fs-8 mt-1'>
          <div>
            <Icon icon="user" className="me-2" size={10} type="solid" />
          </div>
          <div className="fs-8">
            By: {data.creatorUser.fullName}
          </div>
        </FlexRow>
      }

    </div>
  )

  const ReleasedItem = (data: PhoneLine) => (
    <div>
      <div className='d-flex align-items-center'>
        {toPrettyDate(data.releasedAt)}
      </div>
      {
        data.releasedByUser &&
        <FlexRow className='fs-8 mt-1'>
          <div>
            <Icon icon="user" className="me-2" size={10} type="solid" />
          </div>
          <div className="fs-8">
            By: {data.releasedByUser.fullName}
          </div>
        </FlexRow>
      }

    </div>
  )

  useEffect(() => {
    setPage("Released Numbers", "Voice", "Lines");
  }, [])





  return (
    <div className="p-5">
        <DataTable objectName="Line"
          route="/voice/lines"
          addRoute="/voice/lines/search"
          apiRoute={API_ROUTES.Lines}
          refresh={refresh} preventCreate
          extraParams={[{
            name: "released",
            value: true
          }]}
          ascending>
          <DataTableField title="Phone Number" dataValueName="friendlyName" />
          <DataTableField title="Company">
            <LineCompany />
          </DataTableField>
          <DataTableField title="Purchased">
            <CreatedItem />
          </DataTableField>
          <DataTableField title="Released">
            <ReleasedItem />
          </DataTableField>
          <DataTableCustomActions>
            <ReleaseNumberAction />
          </DataTableCustomActions>
          <DataTableCustomActions>
            <ViewNumberLogs />
          </DataTableCustomActions>
        </DataTable>

      <Modal id="modal-restore-number"
        title="Restore Number"
        buttonText="Restore Number"
        buttonColor="success"
        onSubmit={(body, callback) => {
          hub.Put("/voice/lines/restore/" + selectendLine?.id, body, res => {
            // Always handed back, so the button stops spinning whatever the answer was.
            if (callback) callback(res);

            if (res.success) {
              reload();
              toastSuccess(res.data?.routed === false
                ? "Phone number restored. Its inbound route could not be put back; please set it again."
                : "Phone number restored successfully!");
              return;
            }

            // Twilio no longer offers it: say why and offer to remove the record instead.
            if (res.field == "notrestorable") {
              window.closeModal("modal-restore-number");
              restoreErrorSet(res.message);
              setTimeout(() => window.openModal("modal-restore-failed"), 400);
            }
          });
        }}>
        <p>
          Are you sure you want to restore this number?
        </p>
        <div className="bg-light-primary p-4">
          <div className="d-flex align-items-center">
            <Icon icon="phone" className="me-2" type="solid" />
            {selectendLine?.friendlyName}
          </div>
          <div className="d-flex align-items-center mt-4">
            <Icon icon="user" className="me-2" type="solid" />
            {selectendLine?.ownerUser?.fullName}
          </div>
          <div className="d-flex align-items-center mt-4">
            <Icon icon="briefcase" className="me-2" type="solid" />
            {selectendLine?.company?.name}
          </div>
        </div>
      </Modal>

      <Modal id="modal-restore-failed"
        title="Number Cannot Be Restored"
        buttonText="Delete Number Record"
        buttonColor="danger"
        onSubmit={(body, callback) => {
          hub.Delete("/voice/lines/" + selectendLine?.id, res => {
            if (callback) callback(res);
            if (res.success) {
              reload();
              toastSuccess("The number's record has been deleted.");
            }
          });
        }}>
        <p>
          <b>{selectendLine?.friendlyName ?? selectendLine?.phoneNumber}</b> could not be taken back from Twilio.
          A released number goes back to Twilio's pool, and once it is no longer offered it cannot be bought again.
        </p>
        <div className="alert alert-danger">
          {restoreError}
        </div>
        <p className="mb-0">
          You can delete this number's record for good. Calls and messages that went through it stay in their history.
        </p>
      </Modal>

    </div>
  );
}

export default LinesReleased;
